import React, { Component } from 'react';
import {
    Tab,
    Tabs,
    Container,
    Grid,
    Button,
    CircularProgress,
    Dialog,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import TravelPreferences from '../../components/ContentComponents/Preferences/TravelPreferences';
import AssignmentPreferences from '../../components/ContentComponents/Preferences/AssignmentPreferences';
import CommunicationPreferences from '../../components/ContentComponents/Preferences/CommunicationPreferences';
import PaymentElectionPreferences from '../../components/ContentComponents/Preferences/PaymentElectionPreferences';
import SmsPreferences from '../../components/ContentComponents/Preferences/SmsPreferences';
import { COLORS } from '../../utils/Application_Constants';
import { WithContexts } from '../../contexts/withContexts';
import ErrorDialog from '../../components/GlobalComponents/Dialog/ErrorDialog';
import RequiredQuestionDialog from '../../components/GlobalComponents/Dialog/RequiredQuestionDialog';
import { getClinicianPaymentElectionPreferences } from '../../services/ProfileServices/profileService';
import { getConfigurationSetting } from '../../services/AppConfigService';

const styles = (theme) => ({
    root: {
        backgroundColor: COLORS.BLACK_HAZE,
        // marginTop: '80px',
        paddingBottom: '80px',
        minHeight: '800px',
    },
    indicator: {
        backgroundColor: COLORS.DENIM,
        opacity: '1',
    },
    tabsRoot: {
        color: COLORS.SLATE_GRAY,
        [theme.breakpoints.up('md')]: {
            padding: '26px 0',
        },
    },
    progressButtonWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
            paddingLeft: '15px',
        },
    },
    progressButton: {
        marginTop: '25px',
        marginRight: '15px',
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    saveButtonWrapper: {
        display: 'flex',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
        },
    },
    saveButton: {
        marginLeft: '42px',
        marginTop: '25px',
        borderRadius: '0px',
        textTransform: 'none',
        boxShadow: 'none',
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: '20px 32px',
        },
    },
    saveMessage: {
        marginTop: '25px',
        paddingLeft: '1rem',
    },
    errorMessage: {
        color: 'red',
    },
    disabeldInput: {
        opacity: 0.6,
    },
    travelTabStyle: {
        [theme.breakpoints.up('md')]: {
            minWidth: '120px',
        },
    },
    payTabStyle: {
        [theme.breakpoints.up('md')]: {
            minWidth: '120px',
        },
    },
    spinnerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
    dialogConatainer: {
        borderRadius: 2,
        minWidth: 300,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
    },
});

class Profile extends Component {
    constructor(props) {
        super(props);
        const { PageContext: { setPageName } = {} } = this.props;
        setPageName(props.t('common:PAGES.PROFILE'));
        this.state = {
            currentlyActiveTab: 0,
            handleSubmit: null,
            childTab: null,
            flags: {
                missingData: false,
                isCharacterLimit: false,
                dataSaved: false,
                isDirty: false,
                showSaveChanges: false,
                isPendingSave: false,
                targetTab: 0,
                isLoading: false,
                validationError: false,
                navDisabled: false,
                apiError: '',
                errorMessage: '',
            },
            url: {
                location: 'preference',
                id: null,
            },
            showSmsPreferences: false,
        };
    }

    async componentDidMount() {
        const { UserContext: { externalId } = {} } = this.props;
        const { flags: currentFlags } = this.state;
        const flags = { ...currentFlags, isLoading: true };
        this.setState({ flags });

        try {
            const [showSmsPrefs, payPreferences] = await Promise.all([
                getConfigurationSetting('ShowSmsPreferences', true, false),
                getClinicianPaymentElectionPreferences(externalId).catch(() => {
                    this.getUrlParams(); // fallback behavior
                    return null;
                }),
            ]);

            this.setState({ showSmsPreferences: showSmsPrefs });

            if (
                payPreferences?.w9Status !== 'Complete' ||
                payPreferences?.ddStatus !== 'Complete'
            ) {
                this.setState({ url: { status: 'paymentElections' } });

                if (showSmsPrefs) {
                    this.moveToTab(4);
                } else {
                    this.moveToTab(3);
                }
            } else {
                await this.getUrlParams();
            }
            await this.setTabState();
        } finally {
            this.setState((prev) => ({ flags: { ...prev.flags, isLoading: false } }));
        }
    }

    async componentDidUpdate(prevProps) {
        const { match } = this.props;

        if (prevProps.match.params.status !== match.params.status) {
            await this.getUrlParams();
            const showSmsPrefs = await getConfigurationSetting('ShowSmsPreferences', true, false);
            this.setState({
                currentlyActiveTab: this.getLocationIndexByStatus(match.params.status),
                showSmsPreferences: showSmsPrefs,
            });
        }
    }

    setPreferencePageError = (e) => {
        const { flags } = { ...this.state };
        if (
            Object.prototype.hasOwnProperty.call(e, 'title') &&
            Object.prototype.hasOwnProperty.call(e, 'message')
        ) {
            flags.apiError = e;
        } else {
            flags.errorMessage = 'Error loading page';
        }
        flags.isLoading = false;
        flags.isPendingSave = false;
        this.setState({ flags });
    };

    getUrlParams() {
        const { match } = this.props;
        this.setState({ url: { ...match.params } });
    }

    getLocationByIndex(currentIndex) {
        const { showSmsPreferences } = this.state;

        const tabRoutes = showSmsPreferences
            ? ['preference', 'sms', 'email', 'travel', 'paymentElections']
            : ['preference', 'communication', 'travel', 'paymentElections'];

        return tabRoutes[currentIndex] || 'preference';
    }

    setTabState = async () => {
        const { url } = { ...this.state };
        await this.setState({ currentlyActiveTab: this.getLocationIndexByStatus(url.status) });
    };

    getLocationIndexByStatus(status) {
        const { showSmsPreferences } = this.state;

        const statusToIndexMap = showSmsPreferences
            ? {
                  preference: 0,
                  sms: 1,
                  email: 2,
                  travel: 3,
                  paymentElections: 4,
              }
            : {
                  preference: 0,
                  communication: 1,
                  travel: 2,
                  paymentElections: 3,
              };

        return statusToIndexMap[status] ?? 0;
    }

    closeError = () => {
        const state = { ...this.state };
        state.flags.apiError = null;
        this.setState({ ...state });
    };

    updateFlags = (flags) => {
        this.setState({ flags });
    };

    tabChanged = (event, newValue) => {
        const state = { ...this.state };
        if (state.flags.isDirty) {
            state.flags.showSaveChanges = true;
            state.flags.targetTab = newValue;
            event.preventDefault();
            this.setState({ ...state });
        } else {
            const { history } = this.props;
            history.push(`/profile/${this.getLocationByIndex(newValue)}`);
            this.setState({ currentlyActiveTab: newValue });
        }
    };

    setSaveHandler = (child, handler) => {
        const state = { ...this.state };
        state.childTab = child;
        state.handleSubmit = handler;
        // Reset save flags
        state.flags.missingData = false;
        state.flags.isCharacterLimit = false;
        state.flags.dataSaved = false;
        state.flags.isDirty = false;
        state.flags.showSaveChanges = false;
        state.flags.isPendingSave = false;
        this.setState({ ...state });
    };

    setCurrentlyActiveTabHandler = (tabIndexToRender) => {
        this.setState({ currentlyActiveTab: tabIndexToRender });
        window.scrollTo(0, 0);
    };

    moveToTab = (idx) => {
        const state = { ...this.state };
        if (state.flags.isDirty) {
            state.flags.showSaveChanges = true;
            state.flags.targetTab = idx;
            this.setState({ ...state });
        } else {
            this.setCurrentlyActiveTabHandler(idx);
            const { history } = this.props;
            history.push(`/profile/${this.getLocationByIndex(idx)}`);
        }
    };

    onTabClicked = (event, idx) => {
        const state = { ...this.state };
        if (state.flags.isDirty) {
            state.flags.showSaveChanges = true;
            state.flags.targetTab = idx;
            event.preventDefault();
            this.setState({ ...state });
        }
    };

    closeDialog = (btnPressed) => {
        const state = { ...this.state };
        state.flags.showSaveChanges = false;
        if (btnPressed === 'Leave') {
            state.flags.isDirty = false; // allow to move on
        }
        this.setState({ ...state });
        if (btnPressed === 'Leave') {
            this.moveToTab(state.flags.targetTab);
        }
    };

    renderSaveMessage = () => {
        const state = { ...this.state };
        const { t, classes } = this.props;
        if (state.flags.missingData) {
            return (
                <span className={classes.errorMessage}>{t('profile:PROFILE.MISSING_INPUT')}</span>
            );
        }
        if (state.flags.isCharacterLimit) {
            return (
                <span className={classes.errorMessage}>{t('common:MESSAGES.CHARACTER_LIMIT')}</span>
            );
        }
        if (state.flags.dataSaved) {
            return <span>{t('common:MESSAGES.DATA_SAVED')}</span>;
        }
        return null;
    };

    tabRender = (currentIndex) => {
        const { UserContext: { externalId } = {}, t } = this.props;
        const { showSmsPreferences, flags } = this.state;

        const sharedProps = {
            tabHandler: this.setCurrentlyActiveTabHandler,
            flags,
            updateFlags: this.updateFlags,
            setSaveHandler: this.setSaveHandler,
            externalId,
            setPreferencePageError: this.setPreferencePageError,
            t,
        };

        const tabsWithSms = [
            AssignmentPreferences,
            SmsPreferences,
            CommunicationPreferences,
            TravelPreferences,
            PaymentElectionPreferences,
        ];

        const tabsWithoutSms = [
            AssignmentPreferences,
            CommunicationPreferences,
            TravelPreferences,
            PaymentElectionPreferences,
        ];

        const TabComponent = (showSmsPreferences ? tabsWithSms : tabsWithoutSms)[currentIndex];

        return TabComponent ? <TabComponent {...sharedProps} /> : null;
    };

    onCallSubmit = () => {
        const { handleSubmit, childTab } = this.state;
        if (handleSubmit !== null && childTab !== null) {
            handleSubmit.call(childTab);
        }
    };

    renderTabButtons() {
        const { currentlyActiveTab, showSmsPreferences } = this.state;
        const { classes, t } = this.props;

        const tabsWithSms = ['ASSIGNMENT', 'SMS', 'EMAIL', 'TRAVEL', 'PAYMENT_ELECTIONS'];

        const tabsWithoutSms = ['ASSIGNMENT', 'COMMUNICATION', 'TRAVEL', 'PAYMENT_ELECTIONS'];

        const tabs = showSmsPreferences ? tabsWithSms : tabsWithoutSms;
        const isFirstTab = currentlyActiveTab === 0;
        const isLastTab = currentlyActiveTab === tabs.length - 1;

        const buttons = [];

        if (!isFirstTab) {
            buttons.push(
                <Button
                    key="prev"
                    className={classes.progressButton}
                    variant="outlined"
                    color="primary"
                    onClick={() => this.moveToTab(currentlyActiveTab - 1)}
                    startIcon={<ChevronLeft />}
                >
                    {t(`tabs:PROFILE.${tabs[currentlyActiveTab - 1]}`)}
                </Button>
            );
        }

        if (!isLastTab) {
            buttons.push(
                <Button
                    key="next"
                    className={classes.progressButton}
                    variant="contained"
                    color="primary"
                    onClick={() => this.moveToTab(currentlyActiveTab + 1)}
                    endIcon={<ChevronRight />}
                >
                    {t(`tabs:PROFILE.${tabs[currentlyActiveTab + 1]}`)}
                </Button>
            );
        }

        return buttons.length === 1 ? buttons[0] : buttons;
    }

    render() {
        const { classes, t } = this.props;
        const { currentlyActiveTab, flags, handleSubmit, showSmsPreferences } = this.state;
        const tabLabels = showSmsPreferences
            ? ['ASSIGNMENT', 'SMS', 'EMAIL', 'TRAVEL', 'PAYMENT_ELECTIONS']
            : ['ASSIGNMENT', 'COMMUNICATION', 'TRAVEL', 'PAYMENT_ELECTIONS'];
        if (flags.isLoading) {
            return (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            );
        }
        return (
            <div className={classes.root}>
                <ErrorDialog
                    open={!!flags.apiError}
                    onClose={this.closeError}
                    title={!flags.apiError ? null : flags.apiError.title}
                    description={!flags.apiError ? null : flags.apiError.message}
                    errorId={!flags.apiError ? null : flags.apiError.errorId}
                />
                <Container maxWidth="md">
                    <RequiredQuestionDialog
                        open={flags.showSaveChanges}
                        title={t('common:MESSAGES.SAVE_TITLE')}
                        text={t('common:MESSAGES.SAVE_MESSAGE')}
                        buttonTexts={t('common:MESSAGES.SAVE_BUTTONS')}
                        buttonIDs="Stay;Leave"
                        disableBackgroundClick={false}
                        onClose={this.closeDialog}
                    />
                    <Tabs
                        classes={{ indicator: classes.indicator, root: classes.tabsRoot }}
                        value={currentlyActiveTab}
                        onChange={this.tabChanged}
                    >
                        {tabLabels.map((labelKey, index) => (
                            <Tab
                                key={labelKey}
                                label={t(`tabs:PROFILE.${labelKey}`)}
                                onClick={(event) => this.onTabClicked(event, index)}
                                id={`tab-${index + 1}`}
                                className={
                                    labelKey === 'PAYMENT_ELECTIONS'
                                        ? classes.payTabStyle
                                        : undefined
                                }
                            />
                        ))}
                    </Tabs>
                    {this.tabRender(currentlyActiveTab)}

                    <Grid container>
                        <Grid item xs={8} md={2} classes={{ root: classes.saveButtonWrapper }}>
                            <Button
                                className={classes.saveButton}
                                classes={{ disabled: classes.disabeldInput }}
                                variant="contained"
                                color="primary"
                                disabled={
                                    !handleSubmit ||
                                    flags.isPendingSave ||
                                    flags.missingData ||
                                    flags.isCharacterLimit ||
                                    !flags.isDirty
                                }
                                onClick={this.onCallSubmit}
                            >
                                {t('common:BUTTONS.SAVE')}
                            </Button>
                        </Grid>

                        <Grid item xs={4} md={2} classes={{ root: classes.saveMessage }}>
                            {this.renderSaveMessage()}
                        </Grid>

                        <Grid item xs={12} md={8} classes={{ root: classes.progressButtonWrapper }}>
                            {this.renderTabButtons()}
                        </Grid>
                    </Grid>

                    <Dialog open={flags.isPendingSave} disableEscapeKeyDown>
                        <div className={classes.dialogConatainer}>
                            <CircularProgress />
                            <Typography variant="h5">{t('common:MESSAGES.SAVING')}</Typography>
                        </div>
                    </Dialog>
                </Container>
            </div>
        );
    }
}

Profile.propTypes = {
    classes: PropTypes.shape({
        root: PropTypes.string.isRequired,
    }).isRequired,
    t: PropTypes.func.isRequired,
};

export default WithContexts(withRouter(withTranslation()(withStyles(styles)(Profile))));
